import React, { useState, useEffect } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as ProfileIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  QrCodeScanner as ScannerIcon,
  Build as MachinesIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { MobileBottomNavigation } from './MobileBottomNavigation';
import { QRScanner } from './QRScanner';

interface MobileLayoutProps {
  children: React.ReactNode;
}

export const MobileLayout: React.FC<MobileLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAppSelector((state) => state.auth);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showQRScanner, setShowQRScanner] = useState(false);

  // Add mobile viewport meta tag for proper mobile rendering
  useEffect(() => {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(meta);
    }
  }, []);

  // Add mobile-specific styling
  useEffect(() => {
    if (isMobile) {
      document.body.style.paddingBottom = '56px'; // Space for bottom navigation
      document.body.style.overscrollBehavior = 'none'; // Prevent pull-to-refresh
    } else {
      document.body.style.paddingBottom = '';
      document.body.style.overscrollBehavior = '';
    }

    return () => {
      document.body.style.paddingBottom = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isMobile]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
    navigate('/login');
  };

  const handleQRScan = (result: string) => {
    console.log('QR Code scanned:', result);
    
    // Try to parse machine ID from QR code
    try {
      const qrData = JSON.parse(result);
      if (qrData.type === 'machine' && qrData.id) {
        navigate(`/machines/${qrData.id}`);
        return;
      }
    } catch (error) {
      // If not JSON, try to extract machine ID from URL
      const machineIdMatch = result.match(/\/machines\/(\d+)/);
      if (machineIdMatch) {
        navigate(`/machines/${machineIdMatch[1]}`);
        return;
      }
    }
    
    // If no machine ID found, show error or navigate to machines list
    console.warn('Unknown QR code format:', result);
    navigate('/machines');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Maschinenmatrix';
    if (path.startsWith('/machines/') && path.includes('/')) {
      return 'Maschinendetails';
    }
    if (path === '/machines') return 'Maschinen';
    if (path.startsWith('/manuals/')) return 'Anleitung';
    return 'Maschinenmatrix';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar 
          position="sticky"
          sx={{ 
            zIndex: theme.zIndex.drawer + 1,
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          }}
        >
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
              {getPageTitle()}
            </Typography>
            
            <IconButton
              size="large"
              edge="end"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  fontSize: '1rem',
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flex: 1,
          pb: isMobile ? 7 : 0, // Extra padding for bottom navigation on mobile
          minHeight: isMobile ? 'calc(100vh - 64px)' : 'auto',
        }}
      >
        {children}
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileBottomNavigation onQRScanClick={() => setShowQRScanner(true)} />
      )}

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/')}>
          <ListItemIcon>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Home</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => navigate('/machines')}>
          <ListItemIcon>
            <MachinesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Maschinen</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => setShowQRScanner(true)}>
          <ListItemIcon>
            <ScannerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>QR-Code scannen</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profil</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Einstellungen</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Abmelden</ListItemText>
        </MenuItem>
      </Menu>

      {/* QR Scanner */}
      <QRScanner
        open={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScan={handleQRScan}
        title="Maschinen-QR-Code scannen"
      />
    </Box>
  );
};